import PropTypes from 'prop-types';
import { FixedSizeGrid as VirtualGrid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Box, IconButton, Typography } from '@mui/material';
import Iconify from '../iconify';
import LoadingAnimation from '../loader/LoadingAnimation';

const ITEM_SIZE = 56;
// Reserve space for the vertical scrollbar so column widths don't overflow it into a horizontal scrollbar.
const SCROLLBAR_GUTTER = 16;

IconPickerGrid.propTypes = {
  icons: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedIcon: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  height: PropTypes.number,
  loading: PropTypes.bool,
};

export default function IconPickerGrid({ icons, selectedIcon, onSelect, height = 400, loading = false }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <LoadingAnimation wrapperSX={{ display: 'flex', justifyContent: 'center', height: '100%' }} />
      </Box>
    );
  }

  if (!icons.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <Typography color="text.secondary">No icons found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, width: '100%' }}>
      <AutoSizer>
        {({ height: gridHeight, width }) => {
          const usableWidth = Math.max(ITEM_SIZE, width - SCROLLBAR_GUTTER);
          const columnCount = Math.max(1, Math.floor(usableWidth / ITEM_SIZE));
          const columnWidth = usableWidth / columnCount;
          const rowCount = Math.ceil(icons.length / columnCount);

          return (
            <VirtualGrid
              columnCount={columnCount}
              columnWidth={columnWidth}
              height={gridHeight}
              rowCount={rowCount}
              rowHeight={ITEM_SIZE}
              width={width}
            >
              {({ columnIndex, rowIndex, style }) => {
                const index = rowIndex * columnCount + columnIndex;
                const icon = icons[index];
                if (!icon) return null;

                return (
                  <Box
                    style={style}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  >
                    <IconButton
                      color="primary"
                      sx={(theme) => ({
                        backgroundColor: selectedIcon === icon ? theme.palette.primary.lightest : 'transparent',
                      })}
                      onClick={() => onSelect(icon)}
                    >
                      <Iconify icon={icon} width={40} height={40} />
                    </IconButton>
                  </Box>
                );
              }}
            </VirtualGrid>
          );
        }}
      </AutoSizer>
    </Box>
  );
}
